import React, { useEffect, useRef, useState } from 'react';
import KIQHeader from './KIQHeader';
import KIQHome from './KIQHome';

// KIQ Assistant — Fase 3 — Panel contenedor.
//
// Desktop: panel lateral derecho (~420px, dentro del rango 400-460px que
// pide el pedido), overlay detrás que también cierra al hacer click.
// Mobile: experiencia casi de pantalla completa (sección 4 del pedido:
// "no usar una burbuja de chat" — nunca un popup chiquito).
//
// Foco/Escape/overlay: mismo patrón YA probado en components/DarkUI.js
// (DarkModal) — mover el foco al panel al abrir, restaurarlo al cerrar,
// cerrar con Escape, overlay-click cierra. No se inventa un segundo
// mecanismo de accesibilidad de modal.
//
// Scroll lock: reutiliza el MISMO mecanismo que Layout.js ya usa para el
// drawer mobile (document.body.style.overflow='hidden' mientras está
// abierto) — un solo lugar posee ese efecto para no duplicar/pisar el
// bloqueo de scroll del drawer de navegación si ambos llegaran a coexistir
// abiertos (Layout.js cierra el drawer de nav al cambiar de ruta y KIQ vive
// aparte, pero comparten la misma técnica probada).
export default function KIQPanel({ abierto, onCerrar, isMobile, usuario, restaurante }) {
  const panelRef = useRef(null);
  // Shell (design_handoff_shell) — "reiniciar conversación" (nuevo botón del
  // header). Remontar KIQComposer vía `key` limpia TODO su estado interno
  // (mensajes/input) sin tocar ninguna línea de KIQComposer.js ni ningún
  // endpoint — el patrón React estándar para "reiniciar" un componente que
  // ya posee su propio estado encapsulado.
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (!abierto) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [abierto, onCerrar]);

  // Nota: el bloqueo de scroll del body NO vive aquí — Layout.js lo posee
  // en un único efecto compartido con el drawer de navegación (ver
  // Layout.js). Si este componente bloqueara el scroll por su cuenta,
  // dos efectos independientes escribirían document.body.style.overflow y
  // podrían pisarse entre sí (ej.: KIQ abierto bloquea, después el efecto
  // del drawer se re-ejecuta con sidebarOpen=false y lo desbloquea por
  // error, aunque KIQ siga abierto) — exactamente el "comportamiento
  // global duplicado y con bugs" que el pedido de Fase 3 pide evitar
  // (sección 18).
  useEffect(() => {
    if (!abierto) return undefined;
    const elementoPrevio = document.activeElement;
    panelRef.current?.focus();
    return () => { elementoPrevio?.focus?.(); };
  }, [abierto]);

  if (!abierto) return null;

  return (
    <div
      onClick={onCerrar}
      role="presentation"
      data-testid="kiq-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 950,
        background: isMobile ? 'var(--bg)' : 'rgba(8,9,13,.6)',
        display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end',
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="KIQ — Restaurant Intelligence"
        style={{
          // Shell (design_handoff_shell) — la caja flota a 12px de los bordes
          // en desktop (antes: panel lateral pegado, altura completa) en vez
          // de position:relative + flex:justify-end del overlay; en mobile
          // sigue siendo borde a borde, sin cambios.
          position: isMobile ? 'relative' : 'absolute',
          top: isMobile ? undefined : '12px', right: isMobile ? undefined : '12px', bottom: isMobile ? undefined : '12px',
          width: isMobile ? '100%' : 'min(460px, calc(100vw - 24px))',
          height: isMobile ? '100%' : undefined,
          borderRadius: isMobile ? 0 : '26px',
          background: 'var(--modal-bg)',
          border: isMobile ? 'none' : '1px solid var(--modal-border)',
          boxShadow: isMobile ? 'none' : '0 40px 90px rgba(0,0,0,.55), 0 0 0 1px rgba(160,120,255,.16)',
          display: 'flex', flexDirection: 'column',
          outline: 'none', overflow: 'hidden',
          animation: isMobile ? 'none' : 'kiqslide .25s ease both',
        }}
      >
        {/* Halos decorativos — puramente visuales, detrás del contenido
            (z-index más bajo, contenedor con overflow:hidden arriba los
            recorta a la caja). Nunca interceptan clicks (pointerEvents none). */}
        {!isMobile && (
          <>
            <span aria-hidden="true" style={{ position: 'absolute', top: '-120px', left: '-60px', width: '320px', height: '320px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,.35), transparent 70%)', filter: 'blur(10px)', animation: 'kiqglow 6s ease-in-out infinite', pointerEvents: 'none' }} />
            <span aria-hidden="true" style={{ position: 'absolute', top: '-90px', right: '-80px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,45,138,.22), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none' }} />
          </>
        )}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <KIQHeader restauranteNombre={restaurante?.nombre} onReiniciar={() => setResetKey((k) => k + 1)} onCerrar={onCerrar} />
          <div className="kiqscroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <KIQHome usuario={usuario} resetKey={resetKey} />
          </div>
        </div>
      </div>
    </div>
  );
}
